import { Alert, Button, Paper, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AuthHero } from '@/components/AuthHero';
import { apiFetch } from '@/lib/api-client';
import { getSupabaseBrowserClient } from '@/lib/supabase/browser';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';

/**
 * Login page — sends a magic link via POST /api/auth/send-magic-link.
 *
 * Ported from `src/app/auth/login/page.tsx`:
 *  - `useRouter` → `useNavigate`
 *  - `useSearchParams` → `react-router-dom` `useSearchParams`
 *  - `createClient` from `@supabase/ssr` → `getSupabaseBrowserClient`
 *  - bare `fetch('/api/auth/send-magic-link')` → `apiFetch`
 *  - Suspense wrapper removed — not needed in SPA context
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  let redirectTo = searchParams.get('redirectTo') || '/';
  const errorParam = searchParams.get('error');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState<number>(0);

  // Prevent open redirect vulnerability
  if (
    !redirectTo ||
    !redirectTo.startsWith('/') ||
    redirectTo.startsWith('//') ||
    redirectTo.includes('://') ||
    redirectTo.includes('\n') ||
    redirectTo.includes('\r')
  ) {
    redirectTo = '/';
  }

  useEffect(() => {
    void breadcrumb('login_view', { redirect_to_path: redirectTo });

    // Strip hash fragments (e.g., from expired link redirects)
    try {
      if (window.location.hash) {
        const cleanUrl =
          window.location.origin +
          window.location.pathname +
          window.location.search;
        window.history.replaceState(null, '', cleanUrl);
      }
    } catch {
      // ignore
    }

    // Prefill email from query param or last used value
    try {
      const emailFromParam = searchParams.get('email');
      if (emailFromParam) {
        setEmail(emailFromParam);
      } else {
        const stored = window.localStorage.getItem('lastEmail');
        if (stored) setEmail(stored);
      }
    } catch {
      // ignore
    }

    const supabase = getSupabaseBrowserClient();

    // Check if already logged in
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) navigate(redirectTo, { replace: true });
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        navigate(redirectTo, { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, redirectTo, searchParams]);

  // Resend throttle countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn(s => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const sendMagicLink = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    setLocalError(null);
    setSending(true);
    try {
      const res = await apiFetch('/api/auth/send-magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, next: redirectTo }),
      });
      if (!res.ok) {
        throw new Error('Failed to send magic link');
      }
      void breadcrumb('magic_link_request', { outcome: 'sent' });
      setSent(true);
      setResendIn(30);
      try {
        window.localStorage.setItem('lastEmail', email);
      } catch {
        // ignore
      }
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : 'Failed to send magic link',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthHero>
      <Text c="navy.2" size="sm" ta="center" mb={20}>
        Please sign in to continue
      </Text>

      {errorParam && !sent && (
        <Alert color="red" mb={16} title="Sign-in link problem">
          {errorParam === 'invalid_or_expired' ||
          errorParam?.includes('expired')
            ? 'Your sign-in link is invalid or has expired. Please request a new magic link and try again.'
            : 'There was a problem signing you in. Please try again.'}
        </Alert>
      )}

      <Paper withBorder shadow="md" p={30} radius="md">
        {sent ? (
          <Stack gap="md">
            <Alert color="navy" title="Check your email">
              If we have your email we have sent a sign-in link to {email}.
              Please use it to continue.
            </Alert>
            <Button variant="subtle" onClick={() => setSent(false)}>
              Use a different email
            </Button>
          </Stack>
        ) : (
          <form onSubmit={sendMagicLink}>
            {localError && (
              <Alert color="red" mb={12}>
                {localError}
              </Alert>
            )}
            <Stack gap="sm">
              <TextInput
                label="Email"
                type="email"
                placeholder="you@example.com"
                required
                value={email}
                onChange={e => setEmail(e.currentTarget.value)}
              />
              <Button
                type="submit"
                loading={sending}
                disabled={sending || resendIn > 0}
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Send magic link'}
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </AuthHero>
  );
}
